/**
 * Storage service that wraps localStorage with environment guards.
 * Provides safe access to localStorage in browser environments.
 */

import { logger } from '../utils/logger';

const isBrowser = typeof window !== 'undefined' && window.localStorage;

/**
 * Safely get an item from localStorage.
 * @param key The key to retrieve
 * @param defaultValue Default value if key not found or not in browser
 * @returns The stored value or defaultValue
 */
export const getItem = (key: string, defaultValue: string = ''): string => {
  if (!isBrowser) return defaultValue;
  try {
    return localStorage.getItem(key) || defaultValue;
  } catch (error) {
    logger.warn('Failed to read from localStorage:', error);
    return defaultValue;
  }
};

/**
 * Safely set an item in localStorage.
 * @param key The key to set
 * @param value The value to store
 */
export const setItem = (key: string, value: string): void => {
  if (!isBrowser) return;
  try {
    localStorage.setItem(key, value);
  } catch (error) {
    logger.warn('Failed to write to localStorage:', error);
  }
};

/**
 * Safely remove an item from localStorage.
 * @param key The key to remove
 */
export const removeItem = (key: string): void => {
  if (!isBrowser) return;
  try {
    localStorage.removeItem(key);
  } catch (error) {
    logger.warn('Failed to remove from localStorage:', error);
  }
};

// Key constants to avoid stringly-typed duplication
export const WEEKLY_PLAN_KEY = 'hbwl_weekly_plan';
export const GROCERY_LIST_KEY = 'groceryList';
export const TRIAL_KEY = 'hbwl_pantry_trial_start';
export const PREMIUM_KEY = 'hbwl_is_premium';
export const DAILY_MANUAL_KEY = 'hbwl_daily_manual_count';
export const DAILY_SCAN_KEY = 'hbwl_daily_scan_count';
export const DAILY_RECIPE_KEY = 'hbwl_daily_recipe_count';
export const FEEDBACK_KEY = 'hbwl_feedback';